import { useNavigate } from 'react-router-dom';
import { Flame, Trophy } from 'lucide-react';
import type { AttendanceLeaderboardRow } from '@techsofit/api-types';
import { Card, EmptyState, Skeleton, cn } from '@techsofit/ui';

/** Compact week/month check-in ranking — used on Attendance and the dashboard. */
export function CheckInLeaderboard({
  rows,
  loading,
  period,
  onPeriod,
  emptyDescription = 'As soon as members start checking in, they show up here.',
}: {
  rows: AttendanceLeaderboardRow[];
  loading?: boolean;
  period: 'WEEK' | 'MONTH';
  onPeriod: (p: 'WEEK' | 'MONTH') => void;
  emptyDescription?: string;
}) {
  const navigate = useNavigate();

  return (
    <div>
      <div className="mb-3 inline-flex gap-1 rounded-pill bg-surface p-1">
        {(['WEEK', 'MONTH'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPeriod(p)}
            className={cn(
              'rounded-pill px-4 py-1.5 text-sm font-medium transition-colors',
              period === p ? 'bg-card text-ink shadow-card' : 'text-ink-soft hover:text-ink',
            )}
          >
            {p === 'WEEK' ? 'This week' : 'This month'}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-card" />)}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<Trophy className="size-6" aria-hidden />} title="No check-ins yet" description={emptyDescription} />
      ) : (
        <ol className="flex flex-col gap-2">
          {rows.map((r, i) => (
            <li key={r.memberId}>
              <button
                type="button"
                onClick={() => navigate(`/members/${r.memberId}`)}
                className="w-full text-left"
              >
                <Card className="flex items-center gap-3 p-3">
                  <span className={cn(
                    'grid size-8 shrink-0 place-items-center rounded-full text-sm font-semibold tabular-nums',
                    i === 0 ? 'bg-accent text-accent-ink' : 'bg-surface text-ink',
                  )}>
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{r.memberName}</p>
                    <p className="text-xs text-muted">
                      {r.visits} day{r.visits === 1 ? '' : 's'}
                      {r.weekStreak > 0 ? ` · ${r.weekStreak} week streak` : ''}
                    </p>
                  </div>
                  {r.weekStreak >= 2 && (
                    <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-ink">
                      <Flame className="size-3.5" aria-hidden /> {r.weekStreak}
                    </span>
                  )}
                </Card>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
